import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Square } from 'lucide-react';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import type { ActiveTimer } from '../../types';
import { runningClock, runningTimersFirst } from './workLogsPageModel';
import { timerClockStart } from '../timer/timerModel';
import { TimerStartEdit } from '../timer/TimerStartEdit';

/** Re-render every `ms` so a running time stays current. */
export function useNow(ms = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

/**
 * P-10d (D-72, bug 98121) — my active log, highlighted at the top of the list.
 * It used to be a pinned table row that vanished when the page had no logs
 * or the task could not be loaded; now every running timer of mine shows here,
 * in both views, from the timer status alone. "עצירה" opens the same finish
 * window as before.
 */
export default function RunningNowBlock({ timers, onStop }: {
  timers: readonly ActiveTimer[] | null | undefined;
  onStop: (timer: ActiveTimer) => void;
}) {
  const { t } = useTranslation();
  const now = useNow();
  const running = runningTimersFirst(timers);
  if (running.length === 0) return null;
  return (
    <section aria-labelledby="wl-running-title" className="mb-4 overflow-hidden rounded-xl border border-accent bg-accent-soft">
      <h2 id="wl-running-title" className="flex items-center gap-2 px-4 pt-3 text-sm font-semibold text-accent-soft-text">
        <span className="status-dot-active inline-block" aria-hidden="true" />
        {t('workLogs.runningNow.title', { count: running.length })}
      </h2>
      <ul className="divide-y divide-border">
        {running.map((tm) => (
          <li key={tm.id} className="relative flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
            <span className="absolute inset-y-0 start-0 w-0.5 bg-live" aria-hidden="true" />
            <Link
              to={`/tickets/${tm.ticket_id}?from=/work-logs`}
              className="min-w-0 flex-1 truncate font-medium text-text hover:underline"
            >
              <bdi>{tm.ticket_title || t('workLogs.noTitle')}</bdi>
            </Link>
            <span className="whitespace-nowrap text-xs text-text-2 tabular-nums">
              <TimerStartEdit timerId={tm.id} startedAt={String(tm.started_at)} className="text-xs text-text-2" />
            </span>
            <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-text" aria-label={t('workLogs.runningNow.elapsedAria', { value: runningClock(timerClockStart(tm), now) })}>
              <bdi dir="ltr">{runningClock(timerClockStart(tm), now)}</bdi>
            </span>
            <button type="button" onClick={() => onStop(tm)} className="btn btn-secondary btn-sm">
              <Square size={14} aria-hidden="true" />
              {t('workLogs.runningNow.stop')}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
